import { SEV_STYLES, severityFromScore } from "@/lib/severity";
import { Fragment, useMemo } from "react";

export default function Heatmap({ grid, onCellClick, activeCell, className = "" }) {
  const maxN = useMemo(() => Math.max(1, ...Object.values(grid || {})), [grid]);
  const rows = [5, 4, 3, 2, 1]; // impact top-to-bottom
  const cols = [1, 2, 3, 4, 5]; // likelihood

  return (
    <div className={`${className}`} data-testid="risk-heatmap">
      <div className="flex">
        {/* Y-axis label */}
        <div className="flex flex-col justify-between mr-2 py-2">
          <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 rotate-180 [writing-mode:vertical-rl] self-center">Impact →</div>
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-[auto_repeat(5,1fr)] gap-1.5">
            {rows.map((i) => (
              <Fragment key={`row-${i}`}>
                <div className="flex items-center justify-end pr-2 text-xs font-mono font-semibold text-slate-500 tabular-nums">{i}</div>
                {cols.map((l) => {
                  const key = `${l}-${i}`;
                  const n = (grid && grid[key]) || 0;
                  const sev = severityFromScore(l * i);
                  const s = SEV_STYLES[sev];
                  const intensity = n === 0 ? 0.35 : 0.55 + Math.min(0.45, (n / maxN) * 0.45);
                  const isActive = activeCell === key;
                  return (
                    <button
                      key={key}
                      onClick={() => onCellClick?.(key, { likelihood: l, impact: i, severity: sev, count: n })}
                      style={{ background: s.hex, opacity: intensity }}
                      className={`relative aspect-square rounded-lg border-2 flex flex-col items-center justify-center text-white shadow-sm transition-all hover:scale-105 hover:z-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-600 ${
                        isActive ? "ring-4 ring-cyan-500 ring-offset-2 z-10 scale-105" : ""
                      } ${n === 0 ? "border-white/60" : "border-white/80"}`}
                      data-testid={`heat-cell-${l}-${i}`}
                      title={`L${l} × I${i} — ${sev} — ${n} risk${n === 1 ? "" : "s"}`}
                    >
                      <div className="font-display font-extrabold text-xl tabular-nums leading-none drop-shadow-sm">{n}</div>
                      <div className="font-mono text-[9px] uppercase tracking-wider opacity-90 mt-1">L{l}·I{i}</div>
                    </button>
                  );
                })}
              </Fragment>
            ))}
            {/* X labels */}
            <div />
            {cols.map((l) => (
              <div key={`xlabel-${l}`} className="text-center text-xs font-mono font-semibold text-slate-500 tabular-nums pt-1">{l}</div>
            ))}
          </div>
          <div className="text-center mt-2 text-[10px] uppercase tracking-widest font-mono text-slate-500">Likelihood →</div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
        {["Low", "Moderate", "High", "Critical"].map((sev) => (
          <div key={sev} className="flex items-center gap-1.5 text-xs">
            <span className="h-2.5 w-2.5 rounded-sm border" style={{ background: SEV_STYLES[sev].hex, borderColor: SEV_STYLES[sev].hex }} />
            <span className="text-slate-600 font-medium">{sev}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
